import request from '@/config/axios'

/**
 * 营销活动（合并开发后的活动内核）
 *
 * 注意：URL **不带** `/admin-api` 前缀，网关前缀由 `config/axios` 的 baseURL 提供。
 * 权限码与后端 `@PreAuthorize` 一字不差，见 `开发包/activity/activity-menu.sql`。
 * 2026-09-16 营销中心合并开发 P4
 */

/** 活动参与规则（入参与出参共用） */
export interface ActivityRuleVO {
  id?: number
  activityId?: number
  /** 规则类型：1-参与 2-分享加成 3-会员等级限制 */
  ruleType: number
  /** 每日次数上限（0-不限） */
  dayLimit?: number
  /** 活动期间次数上限（0-不限） */
  totalLimit?: number
  /** 每次分享加成的次数 */
  shareAddLimit?: number
  /** 分享加成总上限 */
  shareMaxLimit?: number
  /** 可参与会员等级编号（逗号分隔，空=不限） */
  memberLevelIds?: string
  startTime?: string
  endTime?: string
  config?: Record<string, any>
  /** 状态：0-开启 1-关闭 */
  status?: number
}

/** 活动页模块（入参与出参共用） */
export interface ActivityPageModuleVO {
  id?: number
  activityId?: number
  moduleKey: string
  moduleName?: string
  visible?: boolean
  config?: Record<string, any>
  sort?: number
}

/** 营销活动 VO（对应后端 ActivityRespVO） */
export interface ActivityVO {
  id?: number
  templateId?: number
  /** 玩法编码，如 wheel / grid / fission */
  style?: string
  name?: string
  subTitle?: string
  organizer?: string
  coverUrl?: string
  sponsorLogo?: string
  startTime?: string
  endTime?: string
  /** 活动状态：0-草稿 1-已发布 2-已下线 3-已结束 */
  status?: number
  /** 审核状态：0-待审核 1-通过 2-驳回 */
  auditStatus?: number
  config?: Record<string, any>
  /** 页面模块顺序串（8 位，非随机串） */
  rulesortstr?: string
  shareTitle?: string
  shareDesc?: string
  shareCoverUrl?: string
  contactEnable?: boolean
  contactFields?: string
  followRequired?: boolean
  blacklistEnable?: boolean
  pvCount?: number
  uvCount?: number
  joinCount?: number
  awardCount?: number
  shareCount?: number
  remark?: string
  createTime?: string
  rules?: ActivityRuleVO[]
  pageModules?: ActivityPageModuleVO[]
}

/** 新增/修改请求（wxAppid / wxSecret 仅入参，不回显） */
export interface ActivitySaveReqVO extends ActivityVO {
  wxAppid?: string
  wxSecret?: string
}

// ==================== 活动管理 ====================

/** 获得活动分页 */
export function getActivityPage(params: any) {
  return request.get({ url: '/promotion/activity/page', params })
}

/** 获得活动详情 */
export function getActivity(id: number) {
  return request.get({ url: '/promotion/activity/get', params: { id } })
}

/** 创建活动 */
export function createActivity(data: ActivitySaveReqVO) {
  return request.post({ url: '/promotion/activity/create', data })
}

/** 更新活动 */
export function updateActivity(data: ActivitySaveReqVO) {
  return request.put({ url: '/promotion/activity/update', data })
}

/** 删除活动 */
export function deleteActivity(id: number) {
  return request.delete({ url: '/promotion/activity/delete', params: { id } })
}

/** 批量删除活动 */
export function deleteActivityList(ids: number[]) {
  return request.delete({ url: '/promotion/activity/delete-list', params: { ids: ids.join(',') } })
}

/** 发布活动（后端校验时间窗与奖品池） */
export function publishActivity(id: number) {
  return request.put({ url: '/promotion/activity/publish', params: { id } })
}

/** 下线活动 */
export function offlineActivity(id: number) {
  return request.put({ url: '/promotion/activity/offline', params: { id } })
}

/** 审核活动 */
export function auditActivity(data: { id: number; auditStatus: number; remark?: string }) {
  return request.put({ url: '/promotion/activity/audit', data })
}

/** 复制为草稿 */
export function copyActivity(id: number) {
  return request.post({ url: '/promotion/activity/copy', params: { id } })
}

/**
 * 签发一次性预览令牌
 *
 * 用途：C 端活动页默认只在微信内打开（非微信 UA 会被引导拦截），
 * 但运营每次改配置都发到手机上验收不现实，因此由本接口签发短期令牌，
 * 令牌随链接传入后 C 端放行普通浏览器。令牌带有效期，非微信环境也不放开匿名访问。
 */
export function generatePreviewToken(id: number) {
  return request.post({ url: '/promotion/activity/preview-token', params: { id } })
}
